import { makeAutoObservable, runInAction } from "mobx";
import { agent } from "../api/agent";
import { toast } from "react-toastify";
import {
    CalendarEventDTO,
    CreateCalendarEventDTO,
    UpdateCalendarEventDTO,
    CalendarResourceDTO,
    CalendarStatusDTO,
    CalendarEventQueryParams,
    BookingPriceCalculation,
    BookingPriceResult,
    TimeSlotOccupancy,
    CreateRecurringSeriesDTO,
    UpdateRecurringSeriesDTO,
    CreateRecurringSeriesResultDTO,
    EditRecurringSeriesResultDTO,
    DeleteRecurringSeriesResultDTO,
    DeleteRecurringResult,
    EditRecurringResult,
    CreateRecurringResult,
} from "../models/Calendar";
import i18n from "../../i18n";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function extractErrorMessage(error: any, fallbackKey: string): string {
    const data = error?.response?.data;

    if (data?.errors) {
        const allMessages: string[] = [];
        for (const field of Object.values(data.errors) as string[][]) {
            allMessages.push(...field);
        }
        if (allMessages.length > 0) return allMessages.join(' ');
    }

    if (data?.message && typeof data.message === 'string') return data.message;
    if (typeof data === 'string' && data.length > 0 && data.length < 500) return data;
    if (error?.message && typeof error.message === 'string') return error.message;

    return (i18n as any).t(fallbackKey);
}

// ─── Store ────────────────────────────────────────────────────────────────────

export default class CalendarStore {
    events: CalendarEventDTO[] = [];
    resources: CalendarResourceDTO[] = [];
    statuses: CalendarStatusDTO[] = [];

    // THE ROOT CAUSE FIX:
    //
    // Previously there was a single `loading` flag used for both:
    //   (a) the initial page load (events=[]) — correctly shows a full-page Loader
    //   (b) background reloads after create/edit/delete — should be silent
    //
    // SportAssetCalendar had this guard:
    //   if (loading && !events.length) { return <Loader /> }
    //
    // On the very first recurring series creation, events=[] (empty calendar).
    // When the background reload fired (void loadCalendarDataWithDateRange),
    // it set this.loading=true synchronously. MobX notified SportAssetCalendar,
    // which evaluated loading=true && events.length=0 → returned <Loader>,
    // UNMOUNTING the entire component subtree including RecurringSeriesModal.
    // The modal's state (wizard step, successLockedRef) was destroyed.
    // When the reload finished, the modal remounted fresh at step=0, showing the form.
    //
    // Fix: split into two flags:
    //   `loading`           — initial load only, true only when events=[] and we're fetching
    //   `backgroundLoading` — silent background reload flag, never triggers the full-page Loader
    //
    // SportAssetCalendar's guard `if (loading && !events.length)` now only fires
    // on genuine first-load, never on post-create/edit background refreshes.
    loading = false;
    backgroundLoading = false;

    loadingPrice = false;
    currentPriceCalculation: BookingPriceResult | null = null;
    lastLoadedDateRange: { startDate: Date; endDate: Date } | null = null;
    loadingRecurring = false;

    constructor() {
        makeAutoObservable(this);
    }

    // ── Internal: load with explicit control over which flag to set ──────────

    private loadCalendarData = async (
        params: { startDate: Date; endDate: Date },
        isBackground: boolean,
    ) => {
        if (isBackground) {
            runInAction(() => { this.backgroundLoading = true; });
        } else {
            runInAction(() => { this.loading = true; });
        }

        try {
            const queryParams: CalendarEventQueryParams = {
                startDate: params.startDate.toISOString(),
                endDate: params.endDate.toISOString(),
            };

            const data = await agent.Calendar.list(queryParams);

            runInAction(() => {
                this.events = data.events.map(event => ({
                    ...event,
                    start: new Date(event.start),
                    end: new Date(event.end),
                }));
                this.resources = data.resources;
                this.statuses = data.statuses;
                this.lastLoadedDateRange = params;
                this.loading = false;
                this.backgroundLoading = false;
            });
        } catch (error) {
            console.error('Error loading calendar data:', error);
            runInAction(() => {
                this.loading = false;
                this.backgroundLoading = false;
            });
            toast.error((i18n as any).t('calendar.errors.loadingData'));
        }
    };

    // ── Public: initial load (shows full-page loader) ────────────────────────

    loadCalendarDataWithDateRange = async (params: { startDate: Date; endDate: Date }) => {
        return this.loadCalendarData(params, false);
    };

    // ── Public: background reload (silent, never unmounts UI) ────────────────

    private backgroundReload = () => {
        if (this.lastLoadedDateRange) {
            // void — intentionally non-blocking so callers return immediately
            // and no UI is unmounted while this is in flight
            void this.loadCalendarData(this.lastLoadedDateRange, true);
        }
    };

    // ─────────────────────────────────────────────────────────────────────────

    createEvent = async (event: CreateCalendarEventDTO) => {
        this.loading = true;
        try {
            const createdEvent = await agent.Calendar.create(event);
            runInAction(() => { this.loading = false; });
            this.backgroundReload();
            toast.success((i18n as any).t('calendar.success.eventCreated'));
            return createdEvent;
        } catch (error: any) {
            console.error('Error creating event:', error);
            runInAction(() => { this.loading = false; });
            const msg = extractErrorMessage(error, 'calendar.errors.creatingEvent');
            toast.error(msg);
            throw error;
        }
    };

    updateEvent = async (event: UpdateCalendarEventDTO) => {
        this.loading = true;
        try {
            const updatedEvent = await agent.Calendar.update(event);
            runInAction(() => { this.loading = false; });
            this.backgroundReload();
            toast.success((i18n as any).t('calendar.success.eventUpdated'));
            return updatedEvent;
        } catch (error: any) {
            console.error('Error updating event:', error);
            runInAction(() => { this.loading = false; });
            const msg = extractErrorMessage(error, 'calendar.errors.updatingEvent');
            toast.error(msg);
            throw error;
        }
    };

    deleteEvent = async (id: string) => {
        this.loading = true;
        try {
            await agent.Calendar.delete(id);
            runInAction(() => { this.loading = false; });
            this.backgroundReload();
            toast.success((i18n as any).t('calendar.success.eventDeleted'));
        } catch (error: any) {
            console.error('Error deleting event:', error);
            runInAction(() => { this.loading = false; });
            const msg = extractErrorMessage(error, 'calendar.errors.deletingEvent');
            toast.error(msg);
            throw error;
        }
    };

    calculateBookingPrice = async (calculation: BookingPriceCalculation): Promise<BookingPriceResult | null> => {
        this.loadingPrice = true;
        try {
            const result = await agent.Calendar.calculatePrice(calculation);
            runInAction(() => {
                this.currentPriceCalculation = result;
                this.loadingPrice = false;
            });
            return result;
        } catch (error: any) {
            console.error('Error calculating price:', error);
            runInAction(() => {
                this.currentPriceCalculation = null;
                this.loadingPrice = false;
            });
            const msg = extractErrorMessage(error, 'calendar.errors.calculatingPrice');
            toast.error(msg);
            return null;
        }
    };

    clearPriceCalculation = () => {
        this.currentPriceCalculation = null;
    };

    getTimeSlotOccupancy = (assetId: string, startTime: Date, endTime: Date): TimeSlotOccupancy | null => {
        const resource = this.resources.find(r => r.id === assetId);
        if (!resource) return null;

        const overlappingBookings = this.events.filter(event =>
            event.resourceId === assetId &&
            new Date(event.start) < endTime &&
            new Date(event.end) > startTime
        );

        const usedCapacity = overlappingBookings.reduce((sum, booking) => sum + booking.capacityUsed, 0);
        const occupancyPercentage = (usedCapacity / resource.totalCapacityUnits) * 100;
        const occupancyColor = this.getOccupancyColor(occupancyPercentage);

        return {
            startTime,
            endTime,
            totalCapacity: resource.totalCapacityUnits,
            usedCapacity,
            occupancyPercentage,
            occupancyColor,
            bookings: overlappingBookings,
        };
    };

    getOccupancyColor = (percentage: number): string => {
        if (percentage === 0)   return '#28a745';
        if (percentage <= 60)   return '#ffc107';
        if (percentage < 100)   return '#ff9800';
        return '#dc3545';
    };

    forceReload = async () => {
        if (this.lastLoadedDateRange) {
            await this.loadCalendarData(this.lastLoadedDateRange, false);
        }
    };

    shouldReloadForDate = (date: Date): boolean => {
        if (!this.lastLoadedDateRange) return true;
        const buffer = 7 * 24 * 60 * 60 * 1000;
        return (
            date.getTime() < this.lastLoadedDateRange.startDate.getTime() + buffer ||
            date.getTime() > this.lastLoadedDateRange.endDate.getTime() - buffer
        );
    };

    createRecurringSeries = async (
        dto: CreateRecurringSeriesDTO
    ): Promise<CreateRecurringResult> => {
        runInAction(() => { this.loadingRecurring = true; });
        try {
            const result: CreateRecurringSeriesResultDTO =
                await agent.Calendar.createRecurringSeries(dto);

            runInAction(() => { this.loadingRecurring = false; });

            // Background reload — non-blocking, uses backgroundLoading flag
            // so the full-page Loader in SportAssetCalendar is never triggered
            this.backgroundReload();

            if (result.nothingCreated)
                return { type: 'nothing', data: result };

            if (result.slotsSkipped > 0) {
                toast.success((i18n as any).t('calendar.success.recurringSeriesPartial', {
                    created: result.slotsCreated, skipped: result.slotsSkipped,
                }));
                return { type: 'partial', data: result };
            }

            toast.success((i18n as any).t('calendar.success.recurringSeriesCreated'));
            return { type: 'success', data: result };
        } catch (error: any) {
            runInAction(() => { this.loadingRecurring = false; });
            const msg = extractErrorMessage(error, 'calendar.errors.creatingRecurringSeries');
            toast.error(msg);
            return { type: 'error', message: msg };
        }
    };

    editRecurringSeries = async (
        seriesId: string,
        dto: UpdateRecurringSeriesDTO
    ): Promise<EditRecurringResult> => {
        runInAction(() => { this.loadingRecurring = true; });
        try {
            const result: EditRecurringSeriesResultDTO =
                await agent.Calendar.editRecurringSeries(seriesId, dto);

            runInAction(() => { this.loadingRecurring = false; });
            this.backgroundReload();

            if (result.nothingUpdated)
                return { type: 'nothing', data: result };

            if (result.slotsSkipped > 0) {
                toast.success((i18n as any).t('calendar.success.recurringSeriesEditedPartial', {
                    updated: result.slotsUpdated, skipped: result.slotsSkipped,
                }));
                return { type: 'partial', data: result };
            }

            toast.success((i18n as any).t('calendar.success.recurringSeriesEdited', {
                count: result.slotsUpdated,
            }));
            return { type: 'success', data: result };
        } catch (error: any) {
            runInAction(() => { this.loadingRecurring = false; });
            const msg = extractErrorMessage(error, 'calendar.errors.editingRecurringSeries');
            toast.error(msg);
            return { type: 'error', message: msg };
        }
    };

    deleteRecurringSeries = async (
        seriesId: string
    ): Promise<DeleteRecurringResult> => {
        runInAction(() => { this.loadingRecurring = true; });
        try {
            const result: DeleteRecurringSeriesResultDTO =
                await agent.Calendar.deleteRecurringSeries(seriesId);

            runInAction(() => { this.loadingRecurring = false; });
            this.backgroundReload();

            if (result.pastSlotsProtected > 0) {
                toast.success((i18n as any).t('calendar.success.recurringSeriesDeletedWithPast', {
                    deleted: result.futureSlotsDeleted, protected: result.pastSlotsProtected,
                }));
            } else {
                toast.success((i18n as any).t('calendar.success.recurringSeriesDeleted', {
                    deleted: result.futureSlotsDeleted,
                }));
            }

            return { type: 'success', data: result };
        } catch (error: any) {
            runInAction(() => { this.loadingRecurring = false; });
            const msg = extractErrorMessage(error, 'calendar.errors.deletingRecurringSeries');
            toast.error(msg);
            return { type: 'error', message: msg };
        }
    };

    deleteRecurringFromDate = async (
        seriesId: string,
        fromDate: Date
    ): Promise<DeleteRecurringResult> => {
        runInAction(() => { this.loadingRecurring = true; });
        try {
            const fromDateStr = fromDate.toLocaleDateString('en-CA');
            const result: DeleteRecurringSeriesResultDTO =
                await agent.Calendar.deleteRecurringFromDate(seriesId, fromDateStr);

            runInAction(() => { this.loadingRecurring = false; });
            this.backgroundReload();

            if (result.pastSlotsProtected > 0) {
                toast.success((i18n as any).t('calendar.success.recurringSeriesDeletedWithPast', {
                    deleted: result.futureSlotsDeleted, protected: result.pastSlotsProtected,
                }));
            } else {
                toast.success((i18n as any).t('calendar.success.recurringSeriesDeleted', {
                    deleted: result.futureSlotsDeleted,
                }));
            }

            return { type: 'success', data: result };
        } catch (error: any) {
            runInAction(() => { this.loadingRecurring = false; });
            const msg = extractErrorMessage(error, 'calendar.errors.deletingRecurringSeries');
            toast.error(msg);
            return { type: 'error', message: msg };
        }
    };

    detachEventFromSeries = async (eventId: string): Promise<boolean> => {
        try {
            await agent.Calendar.detachFromSeries(eventId);
            runInAction(() => {
                const evt = this.events.find(e => e.id === eventId);
                if (evt) (evt as any).isDetachedFromSeries = true;
            });
            return true;
        } catch (error: any) {
            const msg = extractErrorMessage(error, 'calendar.errors.detachingFromSeries');
            toast.error(msg);
            return false;
        }
    };
}